import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSubscriptions } from '@/hooks/useSubscriptions';
import { useApiKeys } from '@/hooks/useApiKeys';

const httpMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'] as const;
type Method = typeof httpMethods[number];

const methodColor: Record<string, string> = {
  GET: 'text-emerald-600',
  POST: 'text-brand-600',
  PUT: 'text-amber-600',
  DELETE: 'text-red-500',
  PATCH: 'text-violet-600',
};

const statusColor = (code: number) => {
  if (code >= 500) return 'text-red-500';
  if (code >= 400) return 'text-amber-500';
  if (code >= 200) return 'text-green-500';
  return 'text-gray-500';
};

interface Header { key: string; value: string }

const mockResponses: Record<string, { status: number; body: string; latency: number }> = {
  default: {
    status: 200,
    latency: 128,
    body: '{\n  "success": true,\n  "data": {\n    "id": "res_4f2a1c9b",\n    "status": "completed",\n    "timestamp": "' + new Date().toISOString() + '"\n  },\n  "meta": {\n    "request_id": "req_7c3e2f1a",\n    "latency_ms": 128\n  }\n}',
  },
  error401: {
    status: 401,
    latency: 12,
    body: '{\n  "error": "UNAUTHORIZED",\n  "message": "Invalid or expired API key",\n  "code": 401\n}',
  },
  error400: {
    status: 400,
    latency: 45,
    body: '{\n  "error": "VALIDATION_ERROR",\n  "message": "Required field is missing",\n  "code": 400\n}',
  },
};

export default function ApiTestPanel() {
  const { subscriptions, loading: subsLoading } = useSubscriptions();
  const { keys, loading: keysLoading } = useApiKeys();

  const activeApis = subscriptions.filter(s => s.status === 'active');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [method, setMethod] = useState<Method>('GET');
  const [path, setPath] = useState('/status');
  const [headers, setHeaders] = useState<Header[]>([
    { key: 'Authorization', value: 'Bearer sk_live_YOUR_API_KEY' },
    { key: 'Content-Type', value: 'application/json' },
  ]);
  const [body, setBody] = useState('{\n  "input": "value"\n}');
  const [response, setResponse] = useState<null | { status: number; body: string; latency: number; time: string }>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'headers' | 'body' | 'params'>('headers');

  const selectedSub = activeApis[selectedIdx];
  const apiSlug = selectedSub ? selectedSub.product_name.toLowerCase().replace(/\s+/g, '') : 'api';
  const baseUrl = `https://api.apimarts.com/v1/${apiSlug}`;

  // Auto-fill key from user's active keys
  const matchingKey = keys.find(k => k.product_name === selectedSub?.product_name && k.status === 'active');

  const sendRequest = async () => {
    setLoading(true);
    await new Promise(r => setTimeout(r, 800 + Math.random() * 600));
    const authHeader = headers.find(h => h.key === 'Authorization');
    if (!authHeader?.value || authHeader.value.includes('YOUR_KEY') || authHeader.value === 'Bearer sk_live_YOUR_API_KEY') {
      setResponse({ ...mockResponses.error401, time: new Date().toLocaleTimeString() });
    } else if (!body && method === 'POST') {
      setResponse({ ...mockResponses.error400, time: new Date().toLocaleTimeString() });
    } else {
      const r = mockResponses.default;
      setResponse({ ...r, latency: Math.round(r.latency + (Math.random() - 0.5) * 30), time: new Date().toLocaleTimeString() });
    }
    setLoading(false);
  };

  const addHeader = () => setHeaders(h => [...h, { key: '', value: '' }]);
  const updateHeader = (i: number, field: 'key' | 'value', val: string) => setHeaders(h => h.map((x, j) => j === i ? { ...x, [field]: val } : x));
  const removeHeader = (i: number) => setHeaders(h => h.filter((_, j) => j !== i));

  const useKey = () => {
    if (matchingKey) {
      setHeaders(h => h.map(hdr => hdr.key === 'Authorization' ? { ...hdr, value: `Bearer ${matchingKey.key_prefix}` } : hdr));
    }
  };

  if (subsLoading || keysLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-gray-400 text-2xl" />
      </div>
    );
  }

  if (activeApis.length === 0) {
    return (
      <div className="bg-white rounded-xl p-16 text-center">
        <i className="ri-terminal-line text-4xl text-gray-200 block mb-4" />
        <h3 className="font-semibold text-gray-700 mb-2">No active subscriptions</h3>
        <p className="text-sm text-gray-400">Purchase an API from the marketplace to use the tester.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* API Selector */}
      <div className="bg-white rounded-xl p-4 flex items-center gap-3 flex-wrap">
        <div className="text-xs font-semibold text-gray-500">API:</div>
        {activeApis.map((a, i) => (
          <button
            key={a.id}
            onClick={() => { setSelectedIdx(i); setPath('/status'); }}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${selectedIdx === i ? 'bg-sidebar text-white' : 'border border-surface-3 text-gray-500 hover:border-gray-300'}`}
          >
            {a.product_name}
          </button>
        ))}
        <div className="ml-auto text-xs text-gray-400 font-mono">{baseUrl}</div>
      </div>

      {matchingKey && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 flex items-center gap-3">
          <i className="ri-key-2-line text-amber-500 shrink-0" />
          <div className="flex-1 text-xs text-amber-700">
            Found API key for <strong>{selectedSub?.product_name}</strong>: <code className="font-mono">{matchingKey.key_prefix}</code>
          </div>
          <button onClick={useKey} className="text-xs px-3 py-1.5 bg-amber-500 text-white rounded-lg cursor-pointer whitespace-nowrap hover:bg-amber-600 transition-colors">
            Use This Key
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Request Builder */}
        <div className="lg:col-span-3 bg-white rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-3">
            <h3 className="font-semibold text-gray-900 text-sm">Request Builder</h3>
          </div>

          {/* URL Bar */}
          <div className="px-5 py-4 border-b border-surface-3">
            <div className="flex gap-2">
              <select value={method} onChange={e => setMethod(e.target.value as Method)} className={`text-xs font-bold px-3 py-2.5 rounded-lg border border-surface-3 bg-surface-2 focus:outline-none cursor-pointer ${methodColor[method]}`}>
                {httpMethods.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <div className="flex-1 flex items-center border border-surface-3 rounded-lg px-3 bg-surface-2 gap-1">
                <span className="text-[11px] text-gray-400 font-mono shrink-0">{baseUrl}</span>
                <input
                  className="flex-1 text-xs font-mono bg-transparent focus:outline-none text-gray-900 min-w-0"
                  value={path}
                  onChange={e => setPath(e.target.value)}
                  placeholder="/endpoint"
                />
              </div>
              <button
                onClick={sendRequest}
                disabled={loading}
                className="px-4 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5"
              >
                {loading ? <><i className="ri-loader-4-line animate-spin" />Sending…</> : <><i className="ri-send-plane-fill" />Send</>}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-surface-3">
            {(['headers', 'body', 'params'] as const).map(t => (
              <button key={t} onClick={() => setActiveTab(t)} className={`px-5 py-3 text-xs font-medium capitalize transition-colors cursor-pointer whitespace-nowrap ${activeTab === t ? 'border-b-2 border-brand-500 text-brand-600' : 'text-gray-500 hover:text-gray-700'}`}>
                {t} {t === 'headers' && <span className="ml-1 text-[10px] bg-surface-2 px-1.5 py-0.5 rounded-full text-gray-400">{headers.length}</span>}
              </button>
            ))}
          </div>

          <div className="p-5">
            {activeTab === 'headers' && (
              <div className="space-y-2">
                {headers.map((h, i) => (
                  <div key={i} className="flex gap-2">
                    <input className="w-36 text-xs border border-surface-3 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 font-mono" placeholder="Key" value={h.key} onChange={e => updateHeader(i, 'key', e.target.value)} />
                    <input className="flex-1 text-xs border border-surface-3 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 font-mono" placeholder="Value" value={h.value} onChange={e => updateHeader(i, 'value', e.target.value)} />
                    <button onClick={() => removeHeader(i)} className="text-gray-400 hover:text-red-500 cursor-pointer w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors"><i className="ri-delete-bin-line text-sm" /></button>
                  </div>
                ))}
                <button onClick={addHeader} className="text-xs text-brand-600 hover:text-brand-700 cursor-pointer flex items-center gap-1 mt-1"><i className="ri-add-line" />Add Header</button>
              </div>
            )}
            {activeTab === 'body' && (
              <div>
                {method === 'GET' ? (
                  <div className="text-xs text-gray-400 text-center py-6">GET requests do not have a request body</div>
                ) : (
                  <textarea
                    className="w-full h-36 text-xs font-mono border border-surface-3 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400 bg-surface-2 resize-none"
                    value={body}
                    onChange={e => setBody(e.target.value)}
                    placeholder='{"key": "value"}'
                  />
                )}
              </div>
            )}
            {activeTab === 'params' && (
              <div className="text-xs text-gray-400 text-center py-4">No parameters for this endpoint</div>
            )}
          </div>
        </div>

        {/* Response Panel */}
        <div className="lg:col-span-2 bg-white rounded-xl overflow-hidden">
          <div className="px-5 py-4 border-b border-surface-3 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 text-sm">Response</h3>
            {response && (
              <div className="flex items-center gap-3 text-xs">
                <span className={`font-bold ${statusColor(response.status)}`}>{response.status}</span>
                <span className="text-gray-400">{response.latency}ms</span>
                <span className="text-gray-400">{response.time}</span>
              </div>
            )}
          </div>
          <div className="p-5">
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center justify-center py-16 gap-3">
                  <i className="ri-loader-4-line animate-spin text-2xl text-brand-500" />
                  <span className="text-xs text-gray-400">Sending request…</span>
                </motion.div>
              ) : response ? (
                <motion.div key="response" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                  <pre className={`text-xs font-mono rounded-lg p-4 overflow-auto max-h-80 leading-relaxed whitespace-pre ${response.status >= 400 ? 'bg-red-50 text-red-700' : 'bg-gray-900 text-emerald-400'}`}>{response.body}</pre>
                </motion.div>
              ) : (
                <motion.div key="empty" className="flex flex-col items-center justify-center py-16 gap-2 text-center">
                  <i className="ri-send-plane-line text-2xl text-gray-300" />
                  <span className="text-xs text-gray-400">Hit Send to see the response</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
